import React, { useEffect, useRef, Suspense } from 'react'
import { RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { persistQueryClient } from '@tanstack/query-persist-client-core'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

// Import providers and router
import { AppStoreProvider } from '@/stores/AppStoreProvider'
import { initializeStores, cleanupStores } from '@/stores'
import { router } from '@/router'
import { environment } from '@/constants/environment'
import { useSessionStore, useUIStore } from '@/stores'
import { SessionResult } from '@/constants'
import { InitialLoading } from '@/components/LoadingSpinner'
import ToastProvider from '@/components/ToastProvider'
import { useIframeHeight } from '@/hooks/useIframeHeight'
import { setupEmbedConfig } from '@utils/embedConfig'

// Import utilities
import {
  updateStyleVariables,
  appendCampaignStyle,
  appendFavicon,
  injectInlineStyles,
  setupFontSizeListeners,
  isEmbeddedNew as isEmbedded,
  setupModalDetection,
  setupIframeMessageListeners,
  addMobileDeviceDetection,
  postCustomerStatus,
  setupTranslationDetection,
  handleIframeTranslation,
} from '@utils'
import { applyBuilderStyles } from '@utils/builderStyles'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 2,
    },
  },
})

// Persist React Query cache (dev/devtools only) using localStorage
const RQ_CACHE_KEY = 'RQ_CACHE_v1'

if (environment.features.enableQueryPersist) {
  const persister = {
    persistClient: async (client: unknown) => {
      try {
        localStorage.setItem(RQ_CACHE_KEY, JSON.stringify(client))
      } catch (_) {
        // ignore quota errors
      }
    },
    restoreClient: async () => {
      try {
        const cache = localStorage.getItem(RQ_CACHE_KEY)
        return cache ? JSON.parse(cache) : undefined
      } catch (_) {
        return undefined
      }
    },
    removeClient: async () => {
      try {
        localStorage.removeItem(RQ_CACHE_KEY)
      } catch (_) {
        // ignore
      }
    },
  }

  persistQueryClient({
    queryClient,
    persister,
    // Discard snapshots older than 1 hour
    maxAge: 1000 * 60 * 60,
    dehydrateOptions: {
      // Only dehydrate successful queries
      shouldDehydrateQuery: q => q.state.status === 'success',
    },
  })
}

const AppContent = () => {
  const sessionStore = useSessionStore()
  const uiStore = useUIStore()
  const translationDetectorRef = useRef<HTMLElement | null>(null)

  // App state
  const [initialLoading, setInitialLoading] = React.useState(true)
  const [isLanguageReady, setIsLanguageReady] = React.useState(import.meta.env.DEV ? true : false)
  const [loadingStyles, setLoadingStyles] = React.useState(false)

  // Computed values
  const isReady = isLanguageReady && !loadingStyles
  const canLogoutSession = sessionStore.canLogoutSession()

  // Report content height to the host page. Mounted here rather than in a layout.
  useIframeHeight()

  // Attaches the activity listeners that reset the idle timer. Without this the lifeline
  // fires even while the customer is using the page.
  useEffect(() => {
    initializeStores()
    return cleanupStores
  }, [])

  // Handle initial loading — minimal delay for CSS/font init
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false)
    }, 300)

    return () => clearTimeout(timer)
  }, [])

  // Defensive: clear any loading flags that may have been left set by a
  // crashed/closed tab — guarantees the app boots in a clean idle state even
  // if persistence (or a future code path) ever leaves a flag stuck.
  useEffect(() => {
    useUIStore.getState().ready()
  }, [])

  // Setup translation detection (includes language detection from cookie)
  useEffect(() => {
    const { element, cleanup } = setupTranslationDetection(
      () => {
        setIsLanguageReady(true)
      },
      500 // Reduced timeout for better UX
    )

    translationDetectorRef.current = element

    return cleanup
  }, [])

  // Setup font size management
  useEffect(() => {
    const cleanup = setupFontSizeListeners()
    return cleanup
  }, [])

  // Per-session builder stylesheets. The backend ships an array of URLs on
  // session.styles; load them as <link> tags and gate isReady on completion so
  // the UI doesn't paint pre-themed content.
  const sessionStyles = useSessionStore(s => (s.session as any)?.styles as string[] | undefined)
  const campaignId = useSessionStore(s => s.campaign?.id)
  const sessionStylesKey = (sessionStyles || []).join('|')
  const hasLoadedStylesRef = useRef(false)
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const isFirstLoad = !hasLoadedStylesRef.current
      if (isFirstLoad) {
        setLoadingStyles(true)
      }
      try {
        await applyBuilderStyles(sessionStyles)
      } finally {
        if (!cancelled) {
          hasLoadedStylesRef.current = true
          if (isFirstLoad) {
            setLoadingStyles(false)
          }
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Keyed on sessionStylesKey; the styles array itself is a fresh reference each render.
  }, [sessionStylesKey, campaignId])

  // Notify the embedding host (the CDN loader / retention-lite parent frame)
  // any time the combined loading state changes, so it can show/hide its own
  // spinner around our iframe.
  const isLoading = useUIStore(s => s.isLoading)
  const isPopupLoading = useUIStore(s => s.isPopupLoading)
  const retentionLiteLoading = isLoading || isPopupLoading || (initialLoading && !isReady)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.parent) {
      return
    }
    try {
      window.parent.postMessage({ type: 'solvpath_lite_loading', loading: retentionLiteLoading }, '*')
    } catch {
      /* postMessage may throw across origins in restricted hosts; safe to ignore */
    }
  }, [retentionLiteLoading])

  // Setup mobile device detection
  useEffect(() => {
    const cleanup = addMobileDeviceDetection()
    return cleanup
  }, [])

  // Setup iframe communication
  useEffect(() => {
    if (!isEmbedded()) {
      return
    }

    const cleanupEmbedConfig = setupEmbedConfig()
    const cleanupModal = setupModalDetection()
    const cleanupMessages = setupIframeMessageListeners(
      handleIframeTranslation, // onTranslate
      () => {
        // onLogout - navigate to logout or clear session
        sessionStore.logout()
        window.location.href = '/'
      },
      () => {
        // onMainMenu - go home
        sessionStore.logout()
        window.location.href = '/'
      }
    )

    return () => {
      cleanupEmbedConfig()
      cleanupModal()
      cleanupMessages()
    }
  }, [sessionStore])

  // Post customer status to parent on change
  useEffect(() => {
    if (isEmbedded() && canLogoutSession) {
      postCustomerStatus(canLogoutSession)
    }
  }, [canLogoutSession])

  // Initial null on mount is normal (pre-startSession), so only redirect when
  // a previously-existing session becomes null.
  const sessionRef = sessionStore.session
  const hadSessionRef = useRef(false)
  useEffect(() => {
    if (sessionRef) {
      hadSessionRef.current = true
    } else if (hadSessionRef.current && !window.location.pathname.includes('/logout')) {
      router.navigate('/logout', { replace: true })
    }
  }, [sessionRef])

  useEffect(() => {
    const handleExpired = () => {
      if (window.location.pathname.includes('/logout')) {
        return
      }
      sessionStore.logout()
      uiStore.addUIMessage({
        type: 'warning',
        message: 'Your session has expired.',
        persistent: false,
      })
      router.navigate('/logout', { replace: true })
    }
    const handleCritical = (e: Event) => {
      const session = useSessionStore.getState().session as any
      if (!session?.can_enter_error_lifeline) {
        return
      }
      const detail = (e as CustomEvent).detail
      const cb = useUIStore.getState().lifelineCallback
      if (typeof cb === 'function') {
        cb(SessionResult.LifelineError, detail?.message || 'Server error')
      }
    }
    window.addEventListener('session-expired', handleExpired)
    window.addEventListener('critical-error', handleCritical)
    return () => {
      window.removeEventListener('session-expired', handleExpired)
      window.removeEventListener('critical-error', handleCritical)
    }
  }, [sessionStore, uiStore])

  // Handle style variables updates
  useEffect(() => {
    if (uiStore.styleVariables) {
      updateStyleVariables(uiStore.styleVariables)
    }
  }, [uiStore.styleVariables])

  // Handle style URL updates
  useEffect(() => {
    if (uiStore.styleUrl) {
      appendCampaignStyle(uiStore.styleUrl)
    }
  }, [uiStore.styleUrl])

  // Handle favicon updates
  useEffect(() => {
    if (uiStore.faviconUrl) {
      appendFavicon(uiStore.faviconUrl)
    }
  }, [uiStore.faviconUrl])

  // Update document title from campaign name
  useEffect(() => {
    document.title = sessionStore.campaign ? (sessionStore.campaign as any).name : 'Solvpath Support'
  }, [sessionStore.campaign])

  // Inject backend-supplied inline styles. session.style is GrapesJS builder
  // CSS for the current step; campaign.header_style / footer_style apply when
  // the campaign defines custom header/footer HTML.
  const builderStyle = (sessionStore.session as any)?.style
  useEffect(() => {
    injectInlineStyles('builder_style', builderStyle)
  }, [builderStyle])

  useEffect(() => {
    const campaign = sessionStore.campaign as any
    injectInlineStyles('header_style', campaign?.header_content ? campaign?.header_style : null)
    injectInlineStyles('footer_style', campaign?.footer_content ? campaign?.footer_style : null)
  }, [sessionStore.campaign])

  // Handle content width configuration - removed as content_config not in SessionData type

  // Add page wrapper class for sessions - simplified without is_legacy check

  // Show initial loading screen
  if (initialLoading || !isReady) {
    return <InitialLoading isLoading={true} />
  }

  const isCustomerPortal = sessionStore.session?.is_customer_portal

  return (
    <div id="app" className={getCustomClasses()}>
      {/* Main Container */}
      <div id="main" className={isCustomerPortal ? 'sticky-header-content' : ''}>
        {/* Content Container */}
        <div className="main-content">
          <Suspense fallback={<InitialLoading isLoading={true} />}>
            <RouterProvider router={router} />
          </Suspense>
        </div>
      </div>
    </div>
  )

  function getCustomClasses(): string {
    const classes = []

    if (sessionStore.session) {
      // Add session-specific classes based on available session data
      if (sessionStore.session.is_popup) {
        classes.push('popup-mode')
      }
    }

    return classes.join(' ')
  }
}

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AppStoreProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AppStoreProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
